import { useState } from "react";
import { Link, Navigate, useNavigate } from "react-router-dom";
import { useTeamAuth } from "@/lib/teamAuth";
import { ArrowUpRight, Loader2, UserCog } from "lucide-react";
import { toast } from "sonner";

export default function TeamLogin() {
    const { member, loading, login } = useTeamAuth();
    const navigate = useNavigate();
    const [email, setEmail] = useState("");
    const [password, setPassword] = useState("");
    const [busy, setBusy] = useState(false);

    if (loading)
        return (
            <div className="min-h-screen bg-ink flex items-center justify-center">
                <Loader2 className="w-6 h-6 animate-spin text-ember" />
            </div>
        );
    if (member) return <Navigate to="/team" replace />;

    const onSubmit = async (e) => {
        e.preventDefault();
        if (!email || !password) {
            toast.error("Email and password required");
            return;
        }
        setBusy(true);
        try {
            await login(email, password);
            navigate("/team", { replace: true });
        } catch (err) {
            const msg = err?.response?.data?.detail || "Invalid email or password";
            toast.error(typeof msg === "string" ? msg : "Login failed");
        } finally {
            setBusy(false);
        }
    };

    return (
        <main
            data-testid="team-login-page"
            className="min-h-screen bg-ink text-bone flex items-center justify-center px-6 grain"
        >
            <div className="w-full max-w-md">
                <Link to="/" className="flex items-center gap-3 mb-12">
                    <img src="/logo.png" alt="Aximo Studios" className="h-12 w-auto" />
                </Link>

                <div className="flex items-center gap-3 text-xs tracking-[0.25em] uppercase text-ember mb-6">
                    <UserCog className="w-3.5 h-3.5" />
                    Manager portal
                </div>
                <h1 className="font-display text-bone text-5xl tracking-tightest leading-[1] mb-10">
                    Manager
                    <br />
                    <span className="font-serif-i italic text-ember">sign in.</span>
                </h1>

                <form
                    onSubmit={onSubmit}
                    className="space-y-6 border border-white/10 bg-ink-800/40 p-8"
                    data-testid="team-login-form"
                >
                    <label className="block">
                        <span className="block text-[10px] uppercase tracking-[0.25em] text-bone-500 mb-2">
                            Email
                        </span>
                        <input
                            type="email"
                            value={email}
                            onChange={(e) => setEmail(e.target.value)}
                            placeholder="you@aximo.studio"
                            data-testid="team-input-email"
                            className="w-full bg-transparent border-0 border-b border-white/15 focus:border-ember outline-none py-3 text-bone placeholder:text-bone-500 font-display text-lg tracking-tight transition-colors"
                            autoFocus
                        />
                    </label>
                    <label className="block">
                        <span className="block text-[10px] uppercase tracking-[0.25em] text-bone-500 mb-2">
                            Password
                        </span>
                        <input
                            type="password"
                            value={password}
                            onChange={(e) => setPassword(e.target.value)}
                            placeholder="••••••••"
                            data-testid="team-input-password"
                            className="w-full bg-transparent border-0 border-b border-white/15 focus:border-ember outline-none py-3 text-bone placeholder:text-bone-500 font-display text-lg tracking-tight transition-colors"
                        />
                    </label>
                    <button
                        type="submit"
                        disabled={busy}
                        data-testid="team-login-submit"
                        className="btn-ember w-full inline-flex items-center justify-between h-12 px-5 text-sm font-medium tracking-wide disabled:opacity-60"
                    >
                        {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <span>Sign in</span>}
                        <ArrowUpRight className="w-4 h-4" />
                    </button>
                </form>

                <div className="mt-6 flex flex-col gap-2 text-xs text-bone-500">
                    <Link
                        to="/team/forgot-password"
                        data-testid="team-link-forgot"
                        className="text-ember hover:text-bone transition-colors"
                    >
                        Forgot password?
                    </Link>
                    <span>
                        New manager? Check your invite email for the setup link.
                    </span>
                </div>
                <Link
                    to="/"
                    className="mt-8 block text-xs uppercase tracking-[0.25em] text-bone-500 hover:text-ember transition-colors"
                >
                    ← Back to studio
                </Link>
            </div>
        </main>
    );
}
